/**
 * Safe post-login / post-reset redirect helpers.
 * Uses localStorage so the path survives a new tab opened from a set-password email.
 */

const RETURN_KEY = 'dsp.returnTo';

/** Only allow same-origin relative paths (no open redirects). */
export function sanitizeReturnTo(path: string | null | undefined): string | null {
  if (!path) return null;
  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (trimmed.includes('://') || trimmed.includes('\\')) return null;
  if (Array.from(trimmed).some((character) => character.charCodeAt(0) < 32)) return null;
  return trimmed;
}

/** Append `?next=` (or `&next=`) when a safe return path is present. */
export function appendNext(href: string, next: string | null | undefined): string {
  const safe = sanitizeReturnTo(next ?? null);
  if (!safe) return href;
  const sep = href.includes('?') ? '&' : '?';
  return `${href}${sep}next=${encodeURIComponent(safe)}`;
}

export function rememberReturnTo(path: string): void {
  const safe = sanitizeReturnTo(path);
  if (!safe) return;
  try {
    localStorage.setItem(RETURN_KEY, safe);
  } catch {
    // ignore
  }
}

export function peekReturnTo(): string | null {
  try {
    return sanitizeReturnTo(localStorage.getItem(RETURN_KEY));
  } catch {
    return null;
  }
}

/** Read and clear the stored return path. */
export function consumeReturnTo(): string | null {
  const value = peekReturnTo();
  try {
    localStorage.removeItem(RETURN_KEY);
  } catch {
    // ignore
  }
  return value;
}

/** Resolve redirect after auth: query `next` wins, then stored return path, else fallback. */
export function resolveReturnTo(nextFromQuery: string | null | undefined, fallback = '/'): string {
  return sanitizeReturnTo(nextFromQuery) ?? consumeReturnTo() ?? sanitizeReturnTo(fallback) ?? '/';
}
